import React, { useState, useEffect, useReducer, useContext, useMemo, useRef } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},

	ticketsList: {
		flex: 1,
		overflowY: "auto",
		WebkitOverflowScrolling: "touch",
		...theme.scrollbarStyles,
		borderTop: "2px solid rgba(0, 0, 0, 0.12)",
	},

	ticketsListHeader: {
		color: "rgb(67, 83, 105)",
		zIndex: 2,
		backgroundColor: "white",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},

	ticketsCount: {
		fontWeight: "normal",
		color: "rgb(104, 121, 146)",
		marginLeft: "8px",
		fontSize: "14px",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
}));

const mergeEntity = (currentValue, nextValue) => {
	if (!currentValue) return nextValue;
	if (!nextValue) return currentValue;

	return {
		...currentValue,
		...nextValue,
	};
};

const mergeGroupConversation = (currentValue, nextValue) => {
	if (!currentValue) return nextValue;
	if (!nextValue) return currentValue;

	return {
		...currentValue,
		...nextValue,
		members: nextValue.members || currentValue.members,
	};
};

const mergeTicket = (currentTicket, nextTicket) => {
	if (!currentTicket) return nextTicket;
	if (!nextTicket) return currentTicket;

	return {
		...currentTicket,
		...nextTicket,
		contact: mergeEntity(currentTicket.contact, nextTicket.contact),
		user: mergeEntity(currentTicket.user, nextTicket.user),
		queue: mergeEntity(currentTicket.queue, nextTicket.queue),
		whatsapp: mergeEntity(currentTicket.whatsapp, nextTicket.whatsapp),
		connection: mergeEntity(currentTicket.connection, nextTicket.connection),
		groupConversation: mergeGroupConversation(
			currentTicket.groupConversation,
			nextTicket.groupConversation
		),
	};
};

const reducer = (state, action) => {
	if (action.type === "LOAD_TICKETS") {
		const newTickets = action.payload;

		newTickets.forEach(ticket => {
			const ticketIndex = state.findIndex(t => t.id === ticket.id);
			if (ticketIndex !== -1) {
				state[ticketIndex] = mergeTicket(state[ticketIndex], ticket);
				if (ticket.unreadMessages > 0) {
					state.unshift(state.splice(ticketIndex, 1)[0]);
				}
			} else {
				state.push(ticket);
			}
		});

		return [...state];
	}

	if (action.type === "RESET_UNREAD") {
		const ticketId = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state[ticketIndex].unreadMessages = 0;
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = mergeTicket(state[ticketIndex], ticket);
		} else {
			state.unshift(ticket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = mergeTicket(state[ticketIndex], ticket);
			state.unshift(state.splice(ticketIndex, 1)[0]);
		} else {
			state.unshift(ticket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_CONTACT") {
		const contact = action.payload;
		const ticketIndex = state.findIndex(t => t.contactId === contact.id);
		if (ticketIndex !== -1) {
			state[ticketIndex].contact = contact;
		}
		return [...state];
	}

	if (action.type === "DELETE_TICKET") {
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state.splice(ticketIndex, 1);
		}

		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

	const TicketsList = (props) => {
		const {
			status,
			searchParam,
			showAll,
			selectedQueueIds,
			selectedUserIds,
			queueFilterMode,
			userFilterMode,
			includeNoQueue,
			includeUnassigned,
			updateCount,
			style,
		} = props;
	const classes = useStyles();
	const [pageNumber, setPageNumber] = useState(1);
	const [ticketsList, dispatch] = useReducer(reducer, []);
	const [ticketsCount, setTicketsCount] = useState(0);
	const ticketsListRef = useRef([]);
	const { user } = useContext(AuthContext);
	const normalizedQueueIds = useMemo(
		() =>
			(Array.isArray(selectedQueueIds) ? selectedQueueIds : [])
				.map(queueId => Number(queueId))
				.filter(queueId => Number.isFinite(queueId)),
		[selectedQueueIds]
	);
	const normalizedUserIds = useMemo(
		() =>
			(Array.isArray(selectedUserIds) ? selectedUserIds : [])
				.map(selectedUserId => Number(selectedUserId))
				.filter(selectedUserId => Number.isFinite(selectedUserId)),
		[selectedUserIds]
	);
	const requestUserIds = useMemo(
		() => (userFilterMode === "custom" ? normalizedUserIds : []),
		[userFilterMode, normalizedUserIds]
	);
	const shouldApplyUserOrder = status === "open" || status === "pending";
	const isNewestFirst = user?.ticketOrder === "newest_first";

	const orderedTicketsList = useMemo(() => {
		if (!shouldApplyUserOrder) {
			return ticketsList;
		}

		const getTicketTimestamp = ticket => {
			const updatedAtTime = new Date(ticket?.updatedAt || 0).getTime();
			if (Number.isFinite(updatedAtTime) && updatedAtTime > 0) {
				return updatedAtTime;
			}

			const createdAtTime = new Date(ticket?.createdAt || 0).getTime();
			if (Number.isFinite(createdAtTime) && createdAtTime > 0) {
				return createdAtTime;
			}

			return Number(ticket?.id) || 0;
		};

		return [...ticketsList].sort((a, b) => {
			const timeA = getTicketTimestamp(a);
			const timeB = getTicketTimestamp(b);

			if (timeA === timeB) {
				return Number(a?.id || 0) - Number(b?.id || 0);
			}

			return isNewestFirst ? timeB - timeA : timeA - timeB;
		});
	}, [ticketsList, shouldApplyUserOrder, isNewestFirst]);

	useEffect(() => {
		ticketsListRef.current = ticketsList;
	}, [ticketsList]);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	}, [
		status,
		searchParam,
		dispatch,
		showAll,
		selectedQueueIds,
		selectedUserIds,
		queueFilterMode,
		userFilterMode,
		includeNoQueue,
		includeUnassigned,
	]);

	const { tickets, hasMore, loading, count } = useTickets({
		pageNumber,
		searchParam,
		status,
		showAll,
		queueIds: JSON.stringify(normalizedQueueIds),
		queueFilterMode,
		includeNoQueue,
		userIds: JSON.stringify(requestUserIds),
		userFilterMode,
		includeUnassigned,
	});

	useEffect(() => {
		if (!status && !searchParam) return;
		dispatch({
			type: "LOAD_TICKETS",
			payload: tickets,
		});
	}, [tickets]);

	useEffect(() => {
		setTicketsCount(Number(count) || 0);
	}, [count]);

	useEffect(() => {
		const socket = openSocket();

		const ticketMatchesCurrentFilters = (ticket, overrides = {}) => {
			const ticketStatus =
				overrides.status !== undefined ? overrides.status : ticket?.status;
			const ticketQueueId =
				overrides.queueId !== undefined ? overrides.queueId : ticket?.queueId;
			const ticketUserId =
				overrides.userId !== undefined ? overrides.userId : ticket?.userId;
			const normalizedTicketQueueId = Number(ticketQueueId);
			const normalizedTicketUserId = Number(ticketUserId);
			const belongsToStatus = !status || ticketStatus === status;
			const belongsToSelectedQueue =
				queueFilterMode === "all" ||
				(queueFilterMode === "custom" &&
					(ticket?.isGroup ||
						(!ticketQueueId
							? includeNoQueue
							: normalizedQueueIds.includes(normalizedTicketQueueId))));
			const belongsToSelectedUser = showAll
				? userFilterMode === "all"
					? true
					: userFilterMode === "custom" &&
					  ((!ticketUserId && includeUnassigned) ||
							(ticketUserId && normalizedUserIds.includes(normalizedTicketUserId)))
				: normalizedUserIds.length === 0 ||
				  !ticketUserId ||
				  normalizedUserIds.includes(normalizedTicketUserId);

			return (
				!searchParam &&
				belongsToStatus &&
				(!ticketUserId || ticketUserId === user?.id || showAll) &&
				belongsToSelectedUser &&
				belongsToSelectedQueue
			);
		};

		const shouldUpdateTicket = ticket => ticketMatchesCurrentFilters(ticket);

		const wasTicketCounted = data =>
			ticketMatchesCurrentFilters(data.ticket, {
				status: data.oldStatus,
				userId: data.oldUserId,
				queueId: data.oldQueueId,
			});

		const incrementCount = () => {
			setTicketsCount(prevCount => Math.max(Number(prevCount) || 0, 0) + 1);
		};

		const decrementCount = () => {
			setTicketsCount(prevCount => Math.max((Number(prevCount) || 0) - 1, 0));
		};

		socket.on("connect", () => {
			if (status) {
				socket.emit("joinTickets", status);
			} else {
				socket.emit("joinNotification");
			}
		});

		// If socket is already connected, join immediately
		if (socket.connected) {
			if (status) {
				socket.emit("joinTickets", status);
			} else {
				socket.emit("joinNotification");
			}
		}

		socket.on("ticket", data => {
			if (data.action === "updateUnread") {
				dispatch({
					type: "RESET_UNREAD",
					payload: data.ticketId,
				});
			}

			if (data.action === "update") {
				const isCountedNow = shouldUpdateTicket(data.ticket);
				const wasCountedBefore = wasTicketCounted(data);
				const alreadyListed = ticketsListRef.current.some(ticket => ticket.id === data.ticket.id);

				if (isCountedNow) {
					dispatch({
						type: "UPDATE_TICKET",
						payload: data.ticket,
					});
				} else if (alreadyListed) {
					dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
				}

				if (!wasCountedBefore && isCountedNow) {
					incrementCount();
				}

				if (wasCountedBefore && !isCountedNow) {
					decrementCount();
				}
			}

			if (data.action === "delete") {
				const alreadyListed = ticketsListRef.current.some(ticket => ticket.id === data.ticketId);
				dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
				if (alreadyListed || wasTicketCounted(data)) {
					decrementCount();
				}
			}
		});

		socket.on("appMessage", data => {
			if (data.action === "create" && shouldUpdateTicket(data.ticket)) {
				const alreadyListed = ticketsListRef.current.some(ticket => ticket.id === data.ticket.id);
				dispatch({
					type: "UPDATE_TICKET_UNREAD_MESSAGES",
					payload: data.ticket,
				});
				if (!alreadyListed) {
					incrementCount();
				}
			}
		});

		socket.on("contact", data => {
			if (data.action === "update") {
				dispatch({
					type: "UPDATE_TICKET_CONTACT",
					payload: data.contact,
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [
		status,
		searchParam,
		showAll,
		user,
		normalizedQueueIds,
		normalizedUserIds,
		queueFilterMode,
		userFilterMode,
		includeNoQueue,
		includeUnassigned,
	]);

	useEffect(() => {
		if (typeof updateCount === "function") {
			updateCount(Number(ticketsCount) || 0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticketsCount]);

	const loadMore = () => {
		setPageNumber(prevState => prevState + 1);
	};

	const handleScroll = e => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			e.currentTarget.scrollTop = scrollTop - 100;
			loadMore();
		}
	};

	return (
    <Paper className={classes.ticketsListWrapper} style={style}>
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0, paddingBottom: 0 }}>
					{ticketsList.length === 0 && !loading ? (
						<div className={classes.noTicketsDiv}>
							<span className={classes.noTicketsTitle}>
								{i18n.t("ticketsList.noTicketsTitle")}
							</span>
							<p className={classes.noTicketsText}>
								{i18n.t("ticketsList.noTicketsMessage")}
							</p>
						</div>
					) : (
						<>
							{orderedTicketsList.map(ticket => (
								<TicketListItem ticket={ticket} key={ticket.id} />
							))}
						</>
					)}
					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
    </Paper>
	);
};

export default TicketsList;
